import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UserEmail from "../userEmail";
import FileUploader from "./FileUploader";
import MatchResultsList from "./MatchResultsList";
import { downloadZipFile } from "./downloadUtils";
import TrainingUpload from "./TrainingUpload";
import LoginLogoutButtons from "./LoginLogoutButtons";
import ScoreFilterSlider from "./ScoreFilterSlider";
import SortDropdown from "./SortDropdown";
import ThresholdAdjuster from "./ThresholdAdjuster";
import ImportPanel from "./import/ImportPanel";
import ScrubNextSteps from "./ScrubNextSteps";
import WaitingRoom from "./WaitingRoom";
import "./Dashboard.css"; // Make sure this includes .fadeIn styles

// 💡 TrainingInfoPanel Component
function TrainingInfoPanel() {
	return (
		<section
			style={{
				maxWidth: 360,
				margin: "2rem auto",
				padding: "1.5rem 2rem",
				borderRadius: 12,
				boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
				fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
				backgroundColor: "#fff",
			}}>
			<h2
				style={{
					marginBottom: "0.5rem",
					color: "#2d3748",
					textAlign: "center",
				}}>
				Training Mode Overview
			</h2>
			<p style={{ fontSize: 14, color: "#4a5568", marginBottom: "1.5rem" }}>
				You're in <strong>Training Mode</strong>. Label matches below to help
				the system learn. Your input improves match accuracy for future uploads
				by teaching the model which leads are duplicate matches vs none
				duplicates.
			</p>
			<ul
				style={{
					fontSize: 13,
					paddingLeft: 20,
					marginBottom: "1rem",
					color: "#4a5568",
				}}>
				<li>Y = Confirm true duplicates</li>
				<li>N = Not a duplicate</li>
				<li>U = Unsure</li>
			</ul>
			<p style={{ fontSize: 13, color: "#4a5568" }}>
				You have two options for training:
			</p>
			<ol
				style={{
					fontSize: 13,
					paddingLeft: 20,
					color: "#4a5568",
					marginTop: 6,
				}}>
				<li>
					Upload a CSV that includes a <code>training</code> column with values
					<strong> y</strong>, <strong>n</strong>, or <strong>u</strong>.
				</li>
				<li>
					Or use Matching Mode to upload your data, then switch to Training Mode
					to label directly in the UI.
				</li>
			</ol>
		</section>
	);
}

// 💡 ImportInfoPanel Component
function ImportInfoPanel() {
	return (
		<section
			style={{
				maxWidth: 360,
				margin: "2rem auto",
				padding: "1.5rem 2rem",
				borderRadius: 12,
				boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
				fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
				backgroundColor: "#fff",
			}}>
			<h2
				style={{
					marginBottom: "0.5rem",
					color: "#2d3748",
					textAlign: "center",
				}}>
				Import Mode Overview
			</h2>
			<p style={{ fontSize: 14, color: "#4a5568", marginBottom: "1.5rem" }}>
				You're in <strong>Import Mode</strong>. Bulk-import scrubbed lead
				lists into Iris CRM with multi-contact support, a full import ledger
				and safe revert.
			</p>
			<ol
				style={{
					fontSize: 13,
					paddingLeft: 20,
					color: "#4a5568",
					marginTop: 6,
				}}>
				<li>Upload a CSV (ideally the green_new_leads scrub output).</li>
				<li>Map CSV columns to Iris fields (lead- or contact-level).</li>
				<li>Group rows into companies and preview the result.</li>
				<li>Execute — then track or revert it from Import History.</li>
			</ol>
		</section>
	);
}

export default function Dashboard() {
	const [, setFileName] = useState("");
	const [csvResult, setCsvResult] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [mode, setMode] = useState("match"); // 'match' | 'train' | 'import'

	// Waiting room for the scrub: open the moment the upload starts, stays
	// open (with a Continue button) once the result lands, and only closes
	// when the user clicks through. A failed scrub closes it immediately so
	// the usual error toast is what the user sees.
	const [scrubWaitingOpen, setScrubWaitingOpen] = useState(false);
	const [scrubDone, setScrubDone] = useState(false);
	const [filterThreshold, setFilterThreshold] = useState(0.0);
	const [sortOrder, setSortOrder] = useState("score-desc");

	const [thresholds, setThresholds] = useState({
		redThreshold: 0.7,
		yellowThreshold: 0.3,
	});

	const [aboutOpen, setAboutOpen] = useState(false);
	// A scrub list sent to the importer from "What next?" — {id, kind, upload, fileName}.
	const [handoff, setHandoff] = useState(null);

	const handOff = (kind, upload, fileName) => {
		setHandoff({ id: Date.now(), kind, upload, fileName });
		setMode("import");
	};

	const handleThresholdChange = (newVals) => {
		setThresholds((prev) => ({ ...prev, ...newVals }));
	};

	const handleScrubStart = () => {
		setScrubDone(false);
		setScrubWaitingOpen(true);
	};

	const handleScrubError = () => {
		setScrubWaitingOpen(false);
		setScrubDone(false);
	};

	// FileUploader hands us the raw result; keep the waiting room open but
	// flip it to its "done" state instead of revealing results right away.
	const handleScrubResult = (result) => {
		setCsvResult(result);
		setScrubDone(true);
	};

	const handleScrubContinue = () => {
		setScrubWaitingOpen(false);
	};

	const handleDownload = () => {
		if (csvResult?.zip_file_base64) {
			downloadZipFile(csvResult.zip_file_base64, csvResult.zip_filename);
		}
	};

	const toggleMode = () => {
		setMode((prev) => (prev === "match" ? "train" : "match"));
	};

	const sortMatches = (matches) => {
		if (!matches) return [];
		const sorted = [...matches];

		switch (sortOrder) {
			case "score-asc":
				sorted.sort((a, b) => a.match_score - b.match_score);
				break;
			case "score-desc":
				sorted.sort((a, b) => b.match_score - a.match_score);
				break;
			case "company-asc":
				sorted.sort((a, b) =>
					(a.existing.company_name || "").localeCompare(
						b.existing.company_name || ""
					)
				);
				break;
			case "company-desc":
				sorted.sort((a, b) =>
					(b.existing.company_name || "").localeCompare(
						a.existing.company_name || ""
					)
				);
				break;
			default:
				break;
		}
		return sorted;
	};

	// Apply threshold filter first, then sort
	const filteredSortedMatches = (csvResult?.matches || []).filter(
		(m) => m.match_score >= filterThreshold
	);

	const finalMatches = sortMatches(filteredSortedMatches);

	return (
		<div className="dashboardContainer">
			<div className="sideMenu">
				<h1>Welcome!</h1>
				<UserEmail />

				{/* Mode switcher: match / train / import */}
				<div className="modeSwitcher">
					{[
						{ id: "match", label: "Matching" },
						{ id: "train", label: "Training" },
						{ id: "import", label: "Import" },
					].map((m) => (
						<button
							key={m.id}
							type="button"
							className={`modeBtn ${mode === m.id ? "active" : ""}`}
							onClick={() => setMode(m.id)}>
							{m.label}
						</button>
					))}
				</div>

				{/* Mode toggle */}
				{/* <div style={{ marginTop: 30 }}>
					<h4 style={{ marginBottom: 10 }}>
						Mode:{" "}
						<span
							style={{
								fontSize: 14,
								width: 70,
								textAlign: "right",
								fontWeight: 600,
								color: mode === "match" ? "green" : "#E0BC00",
							}}>
							{mode === "match" ? "Matching" : "Training"}
						</span>
					</h4>
					<div className="toggleContainer">
						<label className="switch">
							<input
								type="checkbox"
								checked={mode === "train"}
								onChange={toggleMode}
							/>
							<span className="slider"></span>
						</label>
					</div>
				</div> */}

				{/* About Dropdown */}
				<div
					style={{
						marginTop: 24,
						padding: 12,
						backgroundColor: "rgb(255, 255, 255)",
						borderRadius: 12,
						border: "1px solid #ddd",
						fontSize: 13,
						color: "rgb(45, 55, 72)",
						lineHeight: 1.4,
						maxWidth: 280,
						userSelect: "none",
						boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 10px",
					}}>
					<div
						onClick={() => setAboutOpen(!aboutOpen)}
						style={{
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							fontWeight: 600,
							fontSize: 16,
							marginBottom: aboutOpen ? 12 : 0,
							userSelect: "none",
						}}>
						About This Tool
						<span
							style={{
								display: "inline-block",
								transition: "transform 0.3s ease",
								transform: aboutOpen ? "rotate(90deg)" : "rotate(0deg)",
								fontSize: 20,
								lineHeight: 1,
							}}>
							▶
						</span>
					</div>

					<div
						style={{
							maxHeight: aboutOpen ? 400 : 0,
							overflow: "hidden",
							transition: "max-height 0.4s ease",
						}}>
						<p style={{ margin: "0 0 6px 0" }}>
							Upload and match lead data to identify duplicates.
						</p>
						<p style={{ margin: "0 0 6px 0" }}>
							Switch modes, adjust thresholds, and sort results to fit your
							workflow.
						</p>
					</div>
				</div>

				{/* ▼ NEW: Recommended Headers panel (optional but encouraged) */}
				{mode !== "import" && (
				<div
					style={{
						marginTop: 12,
						padding: 12,
						backgroundColor: "rgb(255, 255, 255)",
						borderRadius: 12,
						border: "1px solid #ddd",
						fontSize: 13,
						color: "rgb(45, 55, 72)",
						lineHeight: 1.5,
						maxWidth: 280,
						userSelect: "none",
						boxShadow: "rgba(0, 0, 0, 0.08) 0px 1px 6px",
					}}>
					<div
						style={{
							fontWeight: 600,
							fontSize: 15,
							marginBottom: 8,
						}}>
						Recommended Headers (optional)
					</div>
					<p style={{ margin: "0 0 6px 0" }}>
						Not required, but these headers improve matching quality:
					</p>
					<ul style={{ paddingLeft: 18, margin: "0 0 8px 0" }}>
						<li>
							<code>company_name</code>
						</li>
						<li>
							<code>first_name</code>
						</li>
						<li>
							<code>last_name</code>
						</li>
						<li>
							<code>email</code>
						</li>
						<li>
							<code>address</code>
						</li>
						<li>
							<code>city</code>
						</li>
						<li>
							<code>normalized_phone</code>
						</li>
					</ul>
					<div
						style={{
							fontSize: 12,
							background: "#f7fafc",
							border: "1px dashed #e2e8f0",
							borderRadius: 8,
							padding: "8px 10px",
							wordBreak: "break-all",
							userSelect: "text",
						}}>
						<span style={{ opacity: 0.8 }}>Example:</span>
						<br />
						<code>
							company_name,first_name,last_name,email,address,city,normalized_phone
						</code>
					</div>
				</div>
				)}
				{/* ▲ END Recommended Headers panel */}

				{/* Show slider only when results exist */}
				<div
					className={`sliderFadeContainer ${
						csvResult && mode !== "import" && !scrubWaitingOpen ? "visible" : "hidden"
					}`}>
					<ScoreFilterSlider
						filterThreshold={filterThreshold}
						onChange={setFilterThreshold}
					/>
				</div>

				{/* Conditionally show ThresholdAdjuster, TrainingInfoPanel or ImportInfoPanel */}
				<div className={`fadeIn`} style={{ marginTop: 30 }}>
					{mode === "match" ? (
						<ThresholdAdjuster onChange={handleThresholdChange} />
					) : mode === "train" ? (
						<TrainingInfoPanel />
					) : (
						<ImportInfoPanel />
					)}
				</div>

				<LoginLogoutButtons />
			</div>

			<div className="content">
				{/* Import mode stays mounted (hidden) so wizard state and batch
				    polling survive switching between modes. */}
				<div
					className="fadeIn"
					style={{ display: mode === "import" ? "block" : "none" }}>
					<ImportPanel active={mode === "import"} handoff={handoff} />
				</div>

				{mode !== "import" && (
				<div className="fadeContainer">
					{mode === "match" ? (
						<div
							key="match"
							className={`fadeIn ${
								csvResult?.matches?.length ? "hasResults" : ""
							}`}>
							<FileUploader
								setFileName={setFileName}
								setCsvResult={handleScrubResult}
								setIsLoading={setIsLoading}
								red_threshold={thresholds.redThreshold}
								yellow_threshold={thresholds.yellowThreshold}
								onStart={handleScrubStart}
								onError={handleScrubError}
							/>
						</div>
					) : (
						<div
							key="train"
							className={`fadeIn ${
								csvResult?.matches?.length ? "hasResults" : ""
							}`}>
							<TrainingUpload />
						</div>
					)}
				</div>
				)}

				{mode === "match" && scrubWaitingOpen && (
					<WaitingRoom
						active={isLoading}
						done={scrubDone}
						title="🔄 Matching in progress…"
						doneMessage={
							csvResult
								? `Done — ${csvResult.match_count} duplicate${
										csvResult.match_count === 1 ? "" : "s"
								  } / ${csvResult.total_new_leads} new lead${
										csvResult.total_new_leads === 1 ? "" : "s"
								  }.`
								: undefined
						}
						onContinue={handleScrubContinue}
					/>
				)}

				{mode !== "import" && csvResult && !scrubWaitingOpen && (
					<div style={{ marginTop: 30 }}>
						<SortDropdown sortOrder={sortOrder} onChange={setSortOrder} />
						<div className="overallInfo">
							<h3>Overall Duplicates Found: {csvResult.match_count}</h3>
							<h3>Overall Unmatched Found: {csvResult.total_new_leads}</h3>
						</div>

						{mode === "match" && (
							<ScrubNextSteps result={csvResult} onHandoff={handOff} />
						)}

						<MatchResultsList
							matches={finalMatches}
							filterThreshold={filterThreshold}
							mode={mode}
							redThreshold={thresholds.redThreshold}
							yellowThreshold={thresholds.yellowThreshold}
						/>

						<div className="btnContainer" style={{ marginTop: 20 }}>
							{mode === "match" ? (
								<button className="downloadBtn" onClick={handleDownload}>
									Download Zip
								</button>
							) : (
								<button className="trainBtn">Train Dedupe</button>
							)}
						</div>
					</div>
				)}
			</div>

			<ToastContainer position="top-right" autoClose={5000} />
		</div>
	);
}
